import { NetworkStatuses, type ReferentPriceType } from '@strata/constants';
import { addCommas } from '@strata/helper-functions';
import { type ColumnDef, GridWithFilters, SecondaryData, SupplierCell } from '@strata/react-grid';
import { useLocation, useNavigate, useParams } from 'react-router';

import { TimeFrameBanner } from 'components/shared/TimeframeBanner';
import { ViewsControls } from 'components/shared/ViewsControls';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import {
    type ServicingPharmacyForProduct,
    useGetServicingPharmaciesForProductQuery,
} from 'store/api/apertureApi';
import { Grids, resetGrid } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridsThunks';
import { resetReferralData } from 'store/referrals/referralListSlice';

const initialSortProperties = [{ id: 'name', desc: false }];

const columns: ColumnDef<ServicingPharmacyForProduct>[] = [
    {
        accessorKey: 'name',
        header: 'Pharmacy Name',
        cell: ({ getValue, row }) => (
            <SupplierCell
                supplierName={getValue()}
                lite={row.original.networkStatus === NetworkStatuses.LITE}
                primary
            />
        ),
    },
    {
        accessorKey: 'referrals',
        header: 'Active Referrals',
        cell: ({ getValue }) => <SecondaryData data={addCommas(getValue())} />,
    },
];

interface ServicingPharmaciesTabProps {
    product?: string;
}

export const ServicingPharmaciesTab = ({ product = undefined }: ServicingPharmaciesTabProps) => {
    const { oid, isBrand, referentPriceType } = useParams() as {
        oid: string;
        isBrand: string;
        referentPriceType: ReferentPriceType;
    };
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { segmentId } = useAppSelector((state) => state.views);
    const { data, isFetching } = useGetServicingPharmaciesForProductQuery({
        segmentId,
        productServiceOid: oid,
        isBrand: isBrand === 'true',
        referentPriceType,
    });

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.SERVICING_PHARMACIES, initialSortProperties);

    const referralsGridState = useGridState(Grids.REFERRALS);

    return (
        <GridWithFilters
            headerContent={<TimeFrameBanner />}
            viewsPaneContent={<ViewsControls />}
            data={data}
            columns={columns}
            isLoading={isFetching}
            sortProperties={sort}
            onSort={updateGridSort}
            enableSearch
            searchText={search}
            onSearch={updateGridSearch}
            activeFilters={activeFilters}
            onFilter={updateGridActiveFilters}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            onRowClick={(rowData) => {
                dispatch(resetGrid(Grids.REFERRALS));
                dispatch(resetReferralData());
                referralsGridState.updateGridActiveFilters?.({
                    is_brand: [isBrand],
                    product_service_description: [product!],
                });
                navigate(AppRoutes.pharmacyDetails(rowData.npi), {
                    state: {
                        backToDestination: {
                            text: 'Product Details',
                            route: location,
                        },
                    },
                });
            }}
        />
    );
};
